import React, { useEffect, useState } from "react";
import Breadcrumb from "../../components/Breadcrumb";
import MessageDialog from "../../components/MessageDialog";
import StorageService from "../../services/StorageService";
import ProjectCategoryService from "../../services/ProjectCategoryService";
import ProjectService from "../../services/ProjectService";
import LeaseTypeService from "../../services/LeaseTypeService";
import ProjectVehicleReportService from "../../services/projectVehicleReportService";
import utility from "../../utils/utility";
import XLSX from "xlsx-js-style";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import logo from '../../assets/images/deviops_logo.png';
import { addPdfLogo } from "../../utils/pdfLogo";

export default function ProjectVehicleReport() {
  const activeCompany = StorageService.activeCompany();
  const companyName = activeCompany?.companyName || "";
  const companyId = activeCompany?.companyId || "";
  const loginData = StorageService.loginData();
  const employeeId = loginData?.employeeId || "";
  const roleId = loginData?.roleId || "";
  const [projectCategoryId, setProjectCategoryId] = useState("");
  const [projectId, setProjectId] = useState("");
  const [leaseTypeId, setLeaseTypeId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [releaseDate, setReleaseDate] = useState("");

  const [projectCategories, setProjectCategories] = useState([]);
  const [projects, setProjects] = useState([]);
  const [leaseTypes, setLeaseTypes] = useState([]);

  const selectedCategory = projectCategories.find(
    (cat) => String(cat.id) === String(projectCategoryId)
  );
  const selectedCategoryCode = selectedCategory?.categoryCode;

  const filteredProjects = projectCategoryId
    ? projects.filter((project) => {
      if (project.projectCategoryId) {
        return String(project.projectCategoryId) === String(projectCategoryId);
      }
      return project.projectCategoryCode === selectedCategoryCode;
    })
    : projects;

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [msgType, setMsgType] = useState("");

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(5);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);

  const formatInputDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getCurrentMonthDateRange = () => {
    const now = new Date();
    const startDate = new Date(now.getFullYear(), now.getMonth(), 1);
    const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return {
      startDate: formatInputDate(startDate),
      endDate: formatInputDate(endDate),
    };
  };

  const showMessage = (text, type = "error") => {
    setMessage(text);
    setMsgType(type);
  };

  const formatProjectDisplay = (project) => {
    if (!project) return "-";

    const projectName = project.projectName || project.name || "";
    const categoryName = project.projectCategoryName || project.categoryName || project.category || "";
    const projectCode = project.projectCode || "";
    const categoryInitial = String(categoryName).trim().charAt(0).toUpperCase();

    if (projectName) {
      if (categoryInitial) {
        return `${categoryInitial}*${projectName}`;
      }

      if (projectCode) {
        const codeInitial = String(projectCode).trim().charAt(0).toUpperCase();
        return `${codeInitial}*${projectName}`;
      }

      return projectName;
    }

    if (projectCode) {
      const codeInitial = String(projectCode).trim().charAt(0).toUpperCase();
      return `${codeInitial}*`;
    }

    return categoryInitial ? `${categoryInitial}*` : "-";
  };

  const loadDropdowns = async () => {
    try {
      const user = StorageService.loginData();
      let projectRes;

      if (Number(user?.roleId) === 1 || Number(user?.roleId) === 2) {
        // Super Admin & Data Admin
        projectRes = await ProjectService.getActive(companyId);
      } else {
        // Employee
        projectRes = await ProjectService.getAssignedProjects(
          companyId,
          user?.employeeId
        );
      }

      const [projectCategoryRes, leaseTypeRes] = await Promise.all([
        ProjectCategoryService.getAll(),
        LeaseTypeService.getActive(),
      ]);

      setProjectCategories(projectCategoryRes?.data || projectCategoryRes || []);

      let projectList = [];
      if (Number(user?.roleId) === 1 || Number(user?.roleId) === 2) {
        projectList = Array.isArray(projectRes) ? projectRes : (projectRes?.data || []);
      } else {
        projectList = projectRes?.data?.data || [];
      }

      setProjects(projectList);
      setLeaseTypes(leaseTypeRes?.data || leaseTypeRes || []);
    } catch (error) {
      console.error(error);
      setProjectCategories([]);
      setProjects([]);
      setLeaseTypes([]);
      showMessage("Failed to load filter dropdowns");
    }
  };

  const loadReport = async (pageNumber = page) => {
    setLoading(true);
    try {
      setReports([]);
      setPage(1);
      const res = await ProjectVehicleReportService.getReport({
        companyId,
        projectCategoryId,
        projectId,
        leaseTypeId,
        startDate,
        releaseDate,
        employeeId,
        roleId,
        page: pageNumber,
        limit
      });

      setReports(res?.data || []);
      setTotalPages(res?.pagination?.totalPages || 0);
      setTotalRecords(res?.pagination?.total || 0);
      setPage(pageNumber);

    } catch (error) {
      setReports([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId) {
      loadDropdowns();
      const { startDate: monthStart, endDate: monthEnd } = getCurrentMonthDateRange();
      setStartDate(monthStart);
      setReleaseDate(monthEnd);
      setTimeout(() => loadReport(), 0);
    }
  }, [companyId]);

  const handleClearFilters = () => {
    setProjectCategoryId("");
    setProjectId("");
    setLeaseTypeId("");
    const { startDate: monthStart, endDate: monthEnd } = getCurrentMonthDateRange();
    setStartDate(monthStart);
    setReleaseDate(monthEnd);
    setReports([]);
    setTimeout(() => loadReport(), 0);
  };

  const getAllReportData = async () => {
    try {
      let allData = [];
      let currentPage = 1;
      let totalPages = 1;

      do {
        const res = await ProjectVehicleReportService.getReport({
          companyId,
          projectCategoryId,
          projectId,
          leaseTypeId,
          startDate,
          releaseDate,
          employeeId,
          roleId,
          page: currentPage,
          limit: 5
        });

        const data = res?.data || [];
        const pagination = res?.pagination || {};

        allData = [...allData, ...data];

        totalPages = pagination.totalPages || 1;
        currentPage++;

      } while (currentPage <= totalPages);

      return allData;

    } catch (error) {
      console.error("PDF error:", error);
      return [];
    }
  };

  const exportExcel = async () => {
    if (!projectCategoryId) {
      showMessage("Project Category is mandatory to download PDF");
      return;
    }

    if (!projectId) {
      showMessage("Project Name is mandatory to download PDF");
      return;
    }
    const allData = await getAllReportData();

    if (!allData.length) {
      showMessage("No data to export");
      return;
    }
    const toNumber = (value) => {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    };
    const sortedData = [...allData].sort((a, b) => {
      const dateA = new Date(a.startDate || 0).getTime();
      const dateB = new Date(b.startDate || 0).getTime();
      return dateA - dateB;
    });

    const wb = XLSX.utils.book_new();
    const ws = {};
    ws["!merges"] = [];

    XLSX.utils.sheet_add_aoa(ws, [[companyName]], { origin: { r: 0, c: 0 } });

    // REPORT DATE (Top Right)
    XLSX.utils.sheet_add_aoa(
      ws,
      [[`Report Date: ${utility.formatExportDateTime(new Date())}`]],
      { origin: { r: 0, c: 8 } }
    );

    // STYLE REPORT DATE
    ws[XLSX.utils.encode_cell({ r: 0, c: 8 })].s = {
      alignment: { horizontal: "right" },
      font: { bold: true, sz: 11 }
    };
    ws[XLSX.utils.encode_cell({ r: 0, c: 0 })].s = {
      font: { bold: true, sz: 12 },
    };

    // Get filter labels
    let projectCategoryLabel = "All";
    if (projectCategoryId) {
      const found = projectCategories.find(pc => String(pc.id) === String(projectCategoryId));
      projectCategoryLabel = found?.categoryName || projectCategoryId;
    }

    let projectLabel = "All";
    if (projectId) {
      const found = projects.find(p => String(p.id || p.projectId) === String(projectId));
      projectLabel = formatProjectDisplay(found || { projectName: projectId });
    }

    let leaseTypeLabel = "All";
    if (leaseTypeId) {
      const found = leaseTypes.find(l => String(l.id) === String(leaseTypeId));
      leaseTypeLabel = found?.leaseType || leaseTypeId;
    }

    let row = 1;

    // STYLES
    const thin = { style: "thin" };
    const center = {
      horizontal: "center",
      vertical: "center",
      wrapText: true
    };

    // TITLE
    XLSX.utils.sheet_add_aoa(ws, [["Project Vehicle Report"]], { origin: { r: row, c: 0 } });
    ws["!merges"].push({ s: { r: row, c: 0 }, e: { r: row, c: 10 } });
    const titleCell = XLSX.utils.encode_cell({ r: row, c: 0 });
    ws[titleCell].s = {
      font: { bold: true, sz: 16 },
      alignment: center,
      border: {
        top: thin,
        bottom: thin,
        left: thin,
        right: thin
      }
    };
    row += 2;

    // FILTER INFO
    XLSX.utils.sheet_add_aoa(ws, [
      ["Project Category:", projectCategoryLabel, "", "Project:", projectLabel],
      ["Lease Type:", leaseTypeLabel, "", "Start Date:", startDate ? utility.formatDateDDMMMYYYY(startDate) : "-"],
      ["", "", "", "Release Date:", releaseDate ? utility.formatDateDDMMMYYYY(releaseDate) : "-"]
    ], { origin: { r: row, c: 0 } });

    const filterLabelStyle = {
      font: { bold: true, sz: 10 },
      alignment: { horizontal: "left", vertical: "center" },
      border: { top: thin, bottom: thin, left: thin, right: thin }
    };

    const filterValueStyle = {
      font: { sz: 10 },
      alignment: { horizontal: "left", vertical: "center" },
      border: { top: thin, bottom: thin, left: thin, right: thin }
    };

    const boldLabelColumns = [0, 3];
    for (let i = 0; i < 3; i++) {
      for (let c = 0; c < 5; c++) {
        const cell = XLSX.utils.encode_cell({ r: row + i, c });
        if (!ws[cell]) continue;
        ws[cell].s = boldLabelColumns.includes(c) ? filterLabelStyle : filterValueStyle;
      }
    }
    row += 4;

    // TABLE HEADER
    const headers = [
      "#",
      "Project Category",
      "Project Name",
      "Vehicle Type",
      "Vehicle Owner",
      "Vehicle Number",
      "Ownership",
      "Lease Type",
      "Fuel Borne By",
      "Hire Rate",
      "Start Date",
      "Release Date"
    ];

    const headerRowIndex = row;
    XLSX.utils.sheet_add_aoa(ws, [headers], { origin: { r: row, c: 0 } });

    headers.forEach((_, colIndex) => {
      const cell = XLSX.utils.encode_cell({ r: row, c: colIndex });
      ws[cell].s = {
        font: { bold: true, sz: 11 },
        alignment: center,
        border: {
          top: thin,
          bottom: thin,
          left: thin,
          right: thin
        }
      };
    });
    row++;

    // TABLE DATA
    sortedData.forEach((reportRow, index) => {
      const hireRateVal = toNumber(reportRow.hireRate);
      const startSerial = utility.convertDateToExcelSerial(reportRow.startDate);
      const releaseSerial = utility.convertDateToExcelSerial(reportRow.releaseDate);

      const rowData = [
        index + 1,
        reportRow.projectCategory || "-",
        reportRow.projectName || "-",
        reportRow.vehicleType || "-",
        reportRow.vehicleOwner || "-",
        reportRow.registrationNo || reportRow.vehicleNumber || "-",
        reportRow.ownership || "-",
        reportRow.leaseType || "-",
        reportRow.fuelBorneBy || "-",
        hireRateVal,
        startSerial || "-",
        releaseSerial || "-"
      ];

      XLSX.utils.sheet_add_aoa(ws, [rowData], { origin: { r: row, c: 0 } });

      rowData.forEach((_, colIndex) => {
        const cell = XLSX.utils.encode_cell({ r: row, c: colIndex });
        const isNumeric = colIndex === 0 || colIndex === 9;
        const isDate = colIndex === 10 || colIndex === 11;
        ws[cell].s = {
          font: { sz: 10 },
          alignment: { horizontal: isNumeric ? "right" : (isDate ? "center" : "left"), vertical: "center" },
          border: {
            top: thin,
            bottom: thin,
            left: thin,
            right: thin
          }
        };

        if (colIndex === 0) {
          ws[cell].t = "n";
          ws[cell].v = index + 1;
          ws[cell].z = "0";
        }

        // Format date columns
        if (colIndex === 10 && startSerial) {
          ws[cell].v = startSerial;
          ws[cell].t = "n";
          ws[cell].z = "dd-mm-yyyy";
        }

        if (colIndex === 11 && releaseSerial) {
          ws[cell].v = releaseSerial;
          ws[cell].t = "n";
          ws[cell].z = "dd-mm-yyyy";
        }

        if (colIndex === 9) {
          ws[cell].t = "n";
          ws[cell].v = hireRateVal;
          ws[cell].z = "#,##0.00";
        }
      });
      row++;
    });

    ws["!autofilter"] = {
      ref: XLSX.utils.encode_range({
        s: { r: headerRowIndex, c: 0 },
        e: { r: row - 1, c: headers.length - 1 }
      })
    };

    // Totals Row
    const totalHireRate = sortedData.reduce((sum, item) => sum + toNumber(item.hireRate), 0);
    const totalsRow = Array(12).fill("");
    totalsRow[8] = "Total:";
    totalsRow[9] = totalHireRate;
    XLSX.utils.sheet_add_aoa(ws, [totalsRow], { origin: { r: row, c: 0 } });

    for (let c = 0; c < 12; c++) {
      const cell = XLSX.utils.encode_cell({ r: row, c });
      const isNumeric = c === 9;
      ws[cell].s = {
        font: { bold: true, sz: 10 },
        alignment: { horizontal: isNumeric || c === 8 ? "right" : "center", vertical: "center" },
        border: { top: thin, bottom: thin, left: thin, right: thin }
      };
      if (isNumeric) {
        ws[cell].t = "n";
        ws[cell].v = totalHireRate;
        ws[cell].z = "#,##0.00";
      }
    }
    row++;

    // Set column widths
    ws["!cols"] = [
      { wch: 8 },
      { wch: 18 },
      { wch: 20 },
      { wch: 15 },
      { wch: 18 },
      { wch: 16 },
      { wch: 16 },
      { wch: 14 },
      { wch: 14 },
      { wch: 16 },
      { wch: 18 },
      { wch: 18 }
    ];

    XLSX.utils.book_append_sheet(wb, ws, "ProjectVehicleReport");
    // XLSX.writeFile(wb, "ProjectVehicleReport.xlsx");
    const projectCategoryName =
      projectCategories.find(
        x => String(x.id) === String(projectCategoryId)
      )?.categoryName || "All";

    const projectName =
      projects.find(
        x => String(x.id || x.projectId) === String(projectId)
      )?.projectName || "All";

    const fileName =
      `ProjectVehicleRPT_${projectCategoryName}_${projectName}_${utility
        .formatExportDateTime(new Date())
        .split(" ")[0]
        .replace(/-/g, "-")}`;

    XLSX.writeFile(wb, `${fileName}.xlsx`);
  };


  const exportPdf = async () => {
    if (!projectCategoryId) {
      showMessage("Project Category is mandatory to download PDF");
      return;
    }

    if (!projectId) {
      showMessage("Project Name is mandatory to download PDF");
      return;
    }
    const allData = await getAllReportData();
    if (!allData.length) {
      showMessage("No data to export");
      return;
    }

    // Get filter labels
    let projectCategoryLabel = "All";
    if (projectCategoryId) {
      const found = projectCategories.find(pc => String(pc.id) === String(projectCategoryId));
      projectCategoryLabel = found?.categoryName || projectCategoryId;
    }

    let projectLabel = "All";
    if (projectId) {
      const found = projects.find(p => String(p.id || p.projectId) === String(projectId));
      projectLabel = formatProjectDisplay(found || { projectName: projectId });
    }

    let leaseTypeLabel = "All";
    if (leaseTypeId) {
      const found = leaseTypes.find(l => String(l.id) === String(leaseTypeId));
      leaseTypeLabel = found?.leaseType || leaseTypeId;
    }

    const doc = new jsPDF("landscape");
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPosition = 15;

    const pageMargin = 14;
    const right = pageWidth - pageMargin;
    doc.setFontSize(12);
    doc.setFont(undefined, "bold");
    // const centerX = pageWidth / 2;
    // const logoW = 9;
    // const logoX = pageMargin; // left logo on first page
    // await addPdfLogo(doc, logo, logoX, 8, logoW, 15);
    // doc.text(companyName, pageMargin, 23);
    // doc.setFontSize(10);
    // doc.setFont(undefined, "normal");
    // doc.text(`Report Date: ${utility.formatDateDMY(new Date())}`, right, 23, { align: "right" });
    // const title = "Project Vehicle Report";
    // doc.setFontSize(18);
    // doc.setFont(undefined, "bold");
    // doc.text(title, centerX, 35, { align: "center" });

    // Logo
    await addPdfLogo(doc, logo, 20, 8, 10, 15);

    // Company Name
    doc.setFontSize(11);
    doc.setFont(undefined, "bold");
    doc.text(companyName, 25, 28, {
      align: "center"
    });

    // Report Date
    const reportDate = utility.formatExportDateTime(new Date());

    doc.setFontSize(10);

    doc.setFont(undefined, "bold");
    doc.text("Report Date:", pageWidth - 70, 18);

    doc.setFont(undefined, "normal");
    doc.text(reportDate, pageWidth - 47, 18);

    // Report Title
    const title = "Project Vehicle Report";

    doc.setFontSize(18);
    doc.setFont(undefined, "bold");
    doc.text(title, pageWidth / 2, 30, {
      align: "center"
    });

    const titleWidth = doc.getTextWidth(title);

    doc.setLineWidth(0.3);
    doc.line(
      (pageWidth - titleWidth) / 2,
      32,
      (pageWidth + titleWidth) / 2,
      32
    );

    yPosition = 40;
    // const titleWidth = doc.getTextWidth(title);
    // doc.setLineWidth(0.5);
    // doc.line(centerX - titleWidth / 2, 36, centerX + titleWidth / 2, 36);

    // yPosition += 20;


    // Header section with filter details
    doc.setFontSize(9);
    doc.setFont(undefined, "bold");

    // Left column
    // let xLeft = 14;

    // ---- CENTER ALIGN BLOCK ----
    let xLeft = 14;
    let xRight = 115;
    let yHeader = yPosition;
    doc.text("Project Category:", xLeft, yHeader);
    doc.setFont(undefined, "normal");
    doc.text(String(projectCategoryLabel), xLeft + 35, yHeader);

    doc.setFont(undefined, "bold");
    doc.text("Project:", xLeft, yHeader + 5);
    doc.setFont(undefined, "normal");
    doc.text(String(projectLabel), xLeft + 35, yHeader + 5);

    doc.setFont(undefined, "bold");
    doc.text("Lease Type:", xLeft, yHeader + 10);
    doc.setFont(undefined, "normal");
    doc.text(String(leaseTypeLabel), xLeft + 35, yHeader + 10);

    // Right column
    // let xRight = pageWidth / 2;
    doc.setFont(undefined, "bold");
    doc.text("Start Date:", xRight, yHeader);
    doc.setFont(undefined, "normal");
    doc.text(startDate ? utility.formatDateDDMMMYYYY(startDate) : "-", xRight + 25, yHeader);

    doc.setFont(undefined, "bold");
    doc.text("Release Date:", xRight, yHeader + 5);
    doc.setFont(undefined, "normal");
    doc.text(releaseDate ? utility.formatDateDDMMMYYYY(releaseDate) : "-", xRight + 25, yHeader + 5);

    yPosition += 25;

    const sortedData = [...allData].sort((a, b) => {
      const dateA = new Date(a.startDate || 0).getTime();
      const dateB = new Date(b.startDate || 0).getTime();
      return dateA - dateB;
    });
    // Table
    const body = sortedData.map((row, index) => [
      index + 1,
      row.projectCategory || "-",
      row.projectName || "-",
      row.vehicleType || "-",
      row.vehicleOwner || "-",
      row.registrationNo || "-",
      row.ownership || "-",
      row.leaseType || "-",
      row.fuelBorneBy || "-",
      row.hireRate || "-",
      utility.formatDateDDMMMYYYY(row.startDate),
      utility.formatDateDDMMMYYYY(row.releaseDate)
    ]);

    // autoTable(doc, {
    //   startY: yPosition,
    //   head: [[
    //     "S.No",
    //     "Project Category",
    //     "Project Name",
    //     "Vehicle Type",
    //     "Vehicle Owner",
    //     "Vehicle Number",
    //     "Ownership",
    //     "Lease Type",
    //     "Fuel Borne By",
    //     "Hire Rate",
    //     "Start Date",
    //     "Release Date"
    //   ]],
    //   body,
    //   theme: "grid",
    //   styles: {
    //     fontSize: 8,
    //     lineColor: [0, 0, 0],
    //     lineWidth: 0.15,
    //     fillColor: [255, 255, 255],
    //     textColor: [0, 0, 0]
    //   },
    //   alternateRowStyles: { fillColor: [255, 255, 255] },
    //   headStyles: { fillColor: [41, 128, 185], textColor: [255, 255, 255], fontStyle: "bold" },
    // });

    autoTable(doc, {
      startY: 55,

      head: [[
        "S.No",
        "Project Category",
        "Project Name",
        "Vehicle Type",
        "Vehicle Owner",
        "Vehicle Number",
        "Ownership",
        "Lease Type",
        "Fuel Borne By",
        "Hire Rate",
        "Start Date",
        "Release Date"
      ]],

      body,

      theme: "grid",

      styles: {
        fontSize: 8,
        lineColor: [0, 0, 0],
        lineWidth: 0.15,
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0]
      },

      alternateRowStyles: {
        fillColor: [255, 255, 255]
      },

      headStyles: {
        fillColor: [230, 230, 230],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        halign: "center"
      },

      columnStyles: {
        0: { halign: "right" },
        1: { halign: "left" },
        2: { halign: "left" },
        3: { halign: "left" },
        4: { halign: "left" },
        5: { halign: "left" },
        6: { halign: "left" },
        7: { halign: "left" },
        8: { halign: "left" },
        9: { halign: "right" },
        10: { halign: "center" },
        11: { halign: "center" }
      },

      margin: {
        top: 35,
        left: 14,
        right: 14
      },

      // didDrawPage: function (data) {

      //   // First page already has header
      //   if (data.pageNumber === 1) return;

      //   const pageWidth = doc.internal.pageSize.getWidth();

      //   const companyNameWidth =
      //     doc.getTextWidth(companyName);

      //   const logoX =
      //     14 + (companyNameWidth - 9) / 2;

      //   doc.addImage(
      //     logo,
      //     "PNG",
      //     logoX,
      //     8,
      //     9,
      //     15
      //   );

      //   doc.setFont(undefined, "bold");
      //   doc.setFontSize(12);

      //   doc.text(
      //     companyName,
      //     14,
      //     28
      //   );

      //   doc.setFontSize(10);

      //   doc.text(
      //     `Report Date: ${utility.formatExportDateTime(new Date())}`,
      //     pageWidth - 14,
      //     18,
      //     { align: "right" }
      //   );

      //   const title =
      //     "Project Vehicle Report";

      //   doc.setFontSize(18);

      //   const titleWidth =
      //     doc.getTextWidth(title);

      //   const centerX =
      //     (pageWidth - titleWidth) / 2;

      //   doc.text(
      //     title,
      //     centerX,
      //     18
      //   );

      //   doc.setLineWidth(0.3);

      //   doc.line(
      //     centerX,
      //     20,
      //     centerX + titleWidth,
      //     20
      //   );
      // }

      didDrawPage: function (data) {

        if (data.pageNumber === 1) return;

        // Logo
        doc.addImage(logo, "PNG", 20, 8, 10, 15);

        // Company Name
        doc.setFontSize(11);
        doc.setFont(undefined, "bold");
        doc.text(companyName, 25, 28, { align: "center" });

        // Report Date
        const reportDate = utility.formatExportDateTime(new Date());

        doc.setFontSize(10);
        doc.setFont(undefined, "bold");
        doc.text("Report Date:", pageWidth - 70, 18);

        doc.setFont(undefined, "normal");
        doc.text(reportDate, pageWidth - 47, 18);

        // Report Title
        const title = "Project Vehicle Report";
        doc.setFontSize(18);
        doc.setFont(undefined, "bold");
        doc.text(title, pageWidth / 2, 27, { align: "center" });

        const titleWidth = doc.getTextWidth(title);
        doc.line(
          (pageWidth - titleWidth) / 2,
          29,
          (pageWidth + titleWidth) / 2,
          29
        );

        // THIS IS IMPORTANT
        data.cursor.y = 32;
      }
    });


    const drawPageNumber = (pageNumber, totalPages) => {
      doc.setFont(undefined, "normal");
      doc.setFontSize(8);
      const text = totalPages 
        ? `Page ${pageNumber} of ${totalPages}` 
        : `Page ${pageNumber}`;
      doc.text(text, pageWidth / 2, pageHeight - 7, { align: "center" });
    };

    const pdfPageCount = doc.getNumberOfPages();

    for (let i = 1; i <= pdfPageCount; i++) {
      doc.setPage(i);
      drawPageNumber(i, pdfPageCount);
      doc.text("Prepared By", 25, pageHeight - 25);
      doc.text("Checked By", pageWidth / 2, pageHeight - 25, { align: "center" });
      doc.text("Approved By", pageWidth - 25, pageHeight - 25, { align: "right" });
    }

    // doc.save("ProjectVehicleReport.pdf");
    const projectCategoryName =
      projectCategories.find(
        x => String(x.id) === String(projectCategoryId)
      )?.categoryName || "All";

    const projectName =
      projects.find(
        x => String(x.id || x.projectId) === String(projectId)
      )?.projectName || "All";

    const fileName =
      `ProjectVehicleRPT_${projectCategoryName}_${projectName}_${utility
        .formatExportDateTime(new Date())
        .split(" ")[0]
        .replace(/-/g, "-")}`;

    doc.save(`${fileName}.pdf`);
  };

  return (
    <>
      <Breadcrumb pageTitle="Project Vehicle Report" secondTitle="Reports" />

      <MessageDialog
        message={message}
        type={msgType}
        setMessage={setMessage}
        setMsgType={setMsgType}
      />

      <div className="card p-4 bg-white shadow-sm">
        <div className="row g-3 mb-3">
          <div className="col-md-3">
            <label className="form-label">Project Category <span className="text-danger">*</span></label>
            <select
              className="form-select"
              value={projectCategoryId}
              onChange={(e) => {
                setProjectCategoryId(e.target.value);
                setProjectId("");
              }}
            >
              <option value="">Select Project Category</option>
              {projectCategories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.categoryName}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-3">
            <label className="form-label">Project Name <span className="text-danger">*</span></label>
            <select
              className="form-select"
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              disabled={!projectCategoryId}
            >
              <option value="">Select Project Name</option>
              {filteredProjects.length === 0 ? (
                <option value="">No Projects Available</option>
              ) : (
                filteredProjects.map((project) => (
                  <option key={project.projectId || project.id} value={project.projectId || project.id}>
                    {formatProjectDisplay(project)}
                  </option>
                ))
              )}
            </select>
          </div>

          <div className="col-md-3">
            <label className="form-label">Lease Type</label>
            <select
              className="form-select"
              value={leaseTypeId}
              onChange={(e) => setLeaseTypeId(e.target.value)}
            >
              <option value="">All</option>
              {leaseTypes.map((leaseType) => (
                <option key={leaseType.id} value={leaseType.id}>
                  {leaseType.leaseType}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-3">
            <label className="form-label">Start Date</label>
            <input
              type="date"
              className="form-control"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          <div className="col-md-3">
            <label className="form-label">Release Date</label>
            <input
              type="date"
              className="form-control"
              value={releaseDate}
              onChange={(e) => setReleaseDate(e.target.value)}
            />
          </div>
        </div>

        <div className="mb-3 d-flex justify-content-end align-items-center">

          <div className="d-flex gap-2">
            <button className="btn btn-secondary" onClick={handleClearFilters}>
              <i className="ti ti-refresh me-1"></i>
              Clear
            </button>

            <button
              className="btn btn-primary"
              onClick={() => {

                if (startDate && releaseDate && startDate > releaseDate) {
                  showMessage(
                    "Start Date should be less than or equal to Release Date",
                    "error"
                  );
                  return;
                }

                loadReport();
              }}
              disabled={loading}
            >
              <i className="ti ti-search me-1"></i>
              {loading ? "Searching..." : "View Report"}
            </button>
          </div>
        </div>
        <hr />
        <div className="mb-3 d-flex justify-content-end align-items-center">
          <div className="d-flex gap-2">
            <button className="btn btn-success" onClick={exportPdf} disabled={loading}>
              <i className="ti ti-file-type-pdf me-1"></i>
              Export PDF
            </button>

            <button className="btn btn-success" onClick={exportExcel} disabled={loading}>
              <i className="ti ti-file-spreadsheet me-1"></i>
              Export Excel
            </button>
          </div>


        </div>

        <div className="table-responsive">
          <table className="table table-bordered table-striped mb-0">
            <thead className="table-light">

              <tr>
                <th>S.No</th>
                <th>Project Category</th>
                <th>Project Name</th>
                <th>Vehicle Type</th>
                <th>Vehicle Owner</th>
                <th>Vehicle Number</th>
                <th>Ownership</th>
                <th>Lease Type</th>
                <th>Fuel Borne By</th>
                <th>Hire Rate</th>
                <th>Start Date</th>
                <th>Release Date</th>
              </tr>

            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="12" className="text-center">
                    <div className="spinner-border text-primary" />
                  </td>
                </tr>
              ) : reports.length === 0 ? (
                <tr>
                  <td colSpan="12" className="text-center">
                    No data found
                  </td>
                </tr>
              ) : (
                reports.map((row, index) => (

                  <tr key={row.id || index}>
                    <td>{(page - 1) * limit + index + 1}</td>
                    <td>{row.projectCategory || "-"}</td>
                    <td>{formatProjectDisplay(row)}</td>
                    <td>{row.vehicleType || "-"}</td>
                    <td>{row.vehicleOwner || "-"}</td>
                    <td>{row.registrationNo || "-"}</td>
                    <td>{row.ownership || "-"}</td>
                    <td>{row.leaseType || "-"}</td>
                    <td>{row.fuelBorneBy || "-"}</td>
                    <td>{row.hireRate || "-"}</td>
                    <td>{utility.formatDateDDMMMYYYY(row.startDate)}</td>
                    <td>{utility.formatDateDDMMMYYYY(row.releaseDate)}</td>
                  </tr>

                ))
              )}
            </tbody>
          </table>
          {totalPages > 1 && (
            <div className="d-flex justify-content-between mt-3">

              <div>
                Total Records: <strong>{totalRecords}</strong>
              </div>

              <div className="d-flex gap-2">
                <button
                  className="btn btn-sm btn-secondary"
                  disabled={page === 1}
                  onClick={() => loadReport(page - 1)}
                >
                  Prev
                </button>

                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i}
                    className={`btn btn-sm ${page === i + 1 ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => loadReport(i + 1)}
                  >
                    {i + 1}
                  </button>
                ))}

                <button
                  className="btn btn-sm btn-secondary"
                  disabled={page === totalPages}
                  onClick={() => loadReport(page + 1)}
                >
                  Next
                </button>
              </div>

            </div>
          )}
        </div>
      </div >
    </>
  );
}
